import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Group,
  Text,
  Badge,
  Button,
  Tabs,
  Table,
  Stack,
  Card,
  ActionIcon,
  Tooltip,
  Avatar,
  Menu,
  SimpleGrid,
  ThemeIcon,
  UnstyledButton,
  Modal,
  Radio,
  TextInput,
  Textarea,
  Autocomplete,
  Loader,
  Alert,
  Divider,
  Center,
} from "@mantine/core";
import {
  IconPlus,
  IconAlertTriangle,
  IconEdit,
  IconTrash,
  IconDownload,
  IconMessageCircle,
  IconDots,
  IconFileTypePdf,
  IconPhoto,
  IconFile,
  IconUpload,
  IconExternalLink,
  IconAlertOctagon,
  IconScale,
  IconPhone,
  IconMail,
  IconChevronDown,
  IconEye,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useDisclosure, useElementSize } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { useQueryClient } from "@tanstack/react-query";
import { DocumentUpload } from "../../../components/accessioning";
import RichTextDiagnosis from "../../../components/pathologist-review/RichTextDiagnosis";
import { useAuth } from "../../../hooks/useAuth";
import { useTaskStore } from "../../../store/task.store";
import {
  useAddComment,
  useUpdateComment,
  useDeleteComment,
} from "../../../hooks/useInternalDiscussions";
import type { UploadedFile } from "../../../types";
import type {
  CaseQaReview,
  UnifiedCommunication,
  CaseFile,
} from "../../../services/cases.service";
import { useAddCommunicationLog } from "../../../hooks/useCases";
import SendOutDetailModal from "../../../components/send-outs/SendOutDetailModal";
import { QaReviewModal } from "../../workflows/QaReviewDetailPage";
import { useCorrectionRequests } from "../../../hooks/useCorrectionRequests";
import type { CorrectionRequest } from "../../../services/correctionRequestService";
import UpdatePatientInfoModal from "../../../components/modals/UpdatePatientInfoModal";
import { CaseFileEditModal } from "../../../components/cases/CaseFileEditModal";
import { caseImagesService } from "../../../services/caseImages.service";
import { sanitizeHtml } from "../../../utils/sanitize";
import { errorRefLabel, correctionRefLabel } from "../../../utils/formatters";
import { getUploadSourceBadge } from "../../../utils/uploadSource";
import PathologistAssignmentHistory from "../../../components/cases/PathologistAssignmentHistory";
import CaseStatusHistoryPanel from "./CaseStatusHistoryPanel";

dayjs.extend(relativeTime);

interface Props {
  activeTab: string;
  onTabChange: (tab: string) => void;
  caseId: string;
  caseData: any;
  notes: any[];
  files: any[];
  errors: any[];
  statusHistory: any[];
  standaloneComments: any[];
  discussions: any[];
  sendOuts: any[];
  communications: UnifiedCommunication[];
  qaReviews: CaseQaReview[];
  isAddingNote: boolean;
  setIsAddingNote: (v: boolean) => void;
  newNoteText: string;
  setNewNoteText: (v: string) => void;
  editingNoteId: string | null;
  setEditingNoteId: (v: string | null) => void;
  editingNoteText: string;
  setEditingNoteText: (v: string) => void;
  onAddNote: () => void;
  onUpdateNote: (noteId: string) => void;
  onDeleteNote: (noteId: string) => void;
  createNoteLoading: boolean;
  updateNoteLoading: boolean;
  onFileDownload: (file: { url: string; name: string }) => void;
  onFileDelete: (fileId: string) => void;
  uploadedDocFiles: UploadedFile[];
  onDocFilesChange: (files: UploadedFile[]) => void;
  onOpenResolveModal: (errorReport: any) => void;
  onReportError: () => void;
}

// Number of fixed core tabs that always render before any optional tabs.
const CORE_TAB_COUNT = 6;
// Headroom reserved for the More dropdown button when overflow tabs exist.
const MORE_BUTTON_WIDTH = 140;
// Prevents sub-pixel rounding and font-weight shifts (active tab bold) from
// triggering unnecessary overflow.
const LAYOUT_TOLERANCE = 10;

const AVATAR_COLORS = [
  "#4C6EF5",
  "#7950F2",
  "#BE4BDB",
  "#E64980",
  "#FA5252",
  "#FD7E14",
  "#FAB005",
  "#40C057",
  "#12B886",
  "#15AABF",
];

function getAvatarColor(name: string): string {
  const hash = name
    .split("")
    .reduce((acc, char) => char.charCodeAt(0) + ((acc * 31) % 1000000), 0);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getNoteBadge(note: any): { label: string; color: string } {
  const role = note.createdBy?.role || note.role || "";
  if (role.toLowerCase().includes("patholog"))
    return { label: "Pathology", color: "violet" };
  if (role.toLowerCase().includes("admin"))
    return { label: "Administrative", color: "blue" };
  return { label: "Clinical", color: "teal" };
}

function getFileIcon(file: any) {
  const mime = (file.mimeType || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  if (mime.includes("pdf") || name.endsWith(".pdf"))
    return { icon: IconFileTypePdf, color: "red" };
  if (mime.startsWith("image/") || /\.(jpe?g|png|gif|webp)$/.test(name))
    return { icon: IconPhoto, color: "blue" };
  return { icon: IconFile, color: "gray" };
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getSendOutStatusBadge(status: string) {
  switch (status) {
    case "pending_approval":
      return { color: "yellow", label: "Pending Approval" };
    case "pending_shipping":
      return { color: "blue", label: "Pending Shipping" };
    case "shipped":
      return { color: "indigo", label: "Sent" };
    case "received_partially":
      return { color: "teal", label: "Partially Received" };
    case "received":
      return { color: "green", label: "Received" };
    case "rejected":
      return { color: "red", label: "Rejected" };
    case "cancelled":
      return { color: "gray", label: "Cancelled" };
    default:
      return { color: "gray", label: status || "Unknown" };
  }
}

function formatRequestType(requestType: string): string {
  switch (requestType) {
    case "physician_request":
      return "Ordering Physician Requesting Consultation";
    case "external_institution":
      return "External Request";
    case "gcp_consult":
      return "Pathologist Requires Expert Consultation";
    case "material_return":
      return "Material Return";
    default:
      return requestType || "—";
  }
}

function getMaterialsSummary(sendOut: any): string {
  const slideCount = sendOut.slidesSent?.length || 0;
  const blockCount = sendOut.blocksSent?.length || 0;
  if (slideCount > 0 && blockCount > 0)
    return `${slideCount} slide${
      slideCount > 1 ? "s" : ""
    } across ${blockCount} block${blockCount > 1 ? "s" : ""}`;
  if (slideCount > 0) return `${slideCount} slide${slideCount > 1 ? "s" : ""}`;
  if (blockCount > 0) return `${blockCount} block${blockCount > 1 ? "s" : ""}`;
  return "No materials";
}

function getDestinationName(sendOut: any): string {
  return (
    sendOut.stainProvider?.name || sendOut.externalInstitution?.name || "—"
  );
}

// ── Correction request helpers ────────────────────────────────────────────────

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim() || "(empty)";
}

const FIELD_LABELS: Record<string, string> = {
  "patient.firstName": "First Name",
  "patient.lastName": "Last Name",
  "patient.dateOfBirth": "Date of Birth",
  "patient.sex": "Sex",
  "patient.medicalRecordNumber": "Medical Record Number",
  "patient.mobilePhone": "Mobile Phone",
  "patient.homePhone": "Home Phone",
  "patient.workPhone": "Work Phone",
  "patient.email": "Email",
  "contact.mobilePhone": "Mobile Phone",
  "contact.homePhone": "Home Phone",
  "contact.workPhone": "Work Phone",
  "contact.email": "Email",
  "address.street": "Street Address",
  "address.state": "State",
  "address.city": "City",
  "address.zipCode": "ZIP Code",
  "primaryInsurance.policyNumber": "Policy Number (Primary)",
  "primaryInsurance.authorizationNumber": "Authorization Number (Primary)",
  "primaryInsurance.groupNumber": "Group Number (Primary)",
  "primaryInsurance.groupName": "Group Name (Primary)",
  "primaryInsurance.patientRelationToInsured": "Relation to Insured (Primary)",
  "secondaryInsurance.policyNumber": "Policy Number (Secondary)",
  "secondaryInsurance.authorizationNumber": "Authorization Number (Secondary)",
  "secondaryInsurance.groupNumber": "Group Number (Secondary)",
  "secondaryInsurance.groupName": "Group Name (Secondary)",
  "secondaryInsurance.patientRelationToInsured":
    "Relation to Insured (Secondary)",
  "caseInfo.externalCaseNumber": "External Case Number",
  "caseInfo.dateReceived": "Date Received",
  "caseInfo.dateCollected": "Date Collected",
  "caseInfo.clinicalHistory": "Clinical History",
  "caseInfo.indications": "Indications",
  "caseInfo.impressions": "Impressions",
  "caseInfo.ruleOuts": "Rule Outs",
  "caseInfo.originatingPracticeId": "Originating Practice",
  "caseInfo.originatingPhysicianId": "Originating Physician",
  "caseInfo.originatingSurgeryCenterId": "Originating Surgery Center",
  "caseInfo.consultationReason": "Consultation Reason",
  "caseInfo.consultationOriginalDiagnosis": "Original Diagnosis",
  "caseInfo.consultationClinicalContext": "Clinical Context",
  "caseInfo.requestingPathologist": "Requesting Pathologist",
  "caseInfo.shippingPathologist": "Shipping Pathologist",
  "caseInfo.shippingStreet": "Shipping Street",
  "caseInfo.shippingState": "Shipping State",
  "caseInfo.shippingCity": "Shipping City",
  "caseInfo.shippingZipCode": "Shipping ZIP Code",
};

export default function CaseDetailTabsV2({
  activeTab,
  onTabChange,
  caseId,
  caseData,
  notes,
  files,
  errors,
  statusHistory,
  standaloneComments,
  discussions,
  sendOuts,
  communications,
  qaReviews,
  isAddingNote,
  setIsAddingNote,
  newNoteText,
  setNewNoteText,
  editingNoteId,
  setEditingNoteId,
  editingNoteText,
  setEditingNoteText,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  createNoteLoading,
  updateNoteLoading,
  onFileDownload,
  onFileDelete,
  uploadedDocFiles,
  onDocFilesChange,
  onOpenResolveModal,
  onReportError,
}: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedTasks } = useTaskStore();
  const canAccessPathologistReview = selectedTasks.some(
    (t) => t.slug === "pathologist-review"
  );
  const currentStatusName = caseData?.currentStatus?.name ?? "";
  const isTcCase = caseData?.accessioningType === "tc_only";
  const reviewQueueStatuses = [
    "Pending Pathologist Review",
    "Pathologist Review",
  ];
  const showReviewInPathologyButton =
    canAccessPathologistReview &&
    user?.id === caseData?.pathologistId &&
    !isTcCase &&
    reviewQueueStatuses.includes(currentStatusName ?? "");

  const { data: correctionData } = useCorrectionRequests(caseId, undefined, {
    includeHistory: true,
  });
  const allRequests: CorrectionRequest[] =
    correctionData?.correctionRequests ?? [];
  const pendingRequests = allRequests.filter((r) => r.status === "pending");

  const [editingCorrection, setEditingCorrection] =
    useState<CorrectionRequest | null>(null);
  const [
    editCorrectionOpened,
    { open: openEditCorrection, close: closeEditCorrection },
  ] = useDisclosure(false);

  const handleOpenEditCorrection = (req: CorrectionRequest) => {
    setEditingCorrection(req);
    openEditCorrection();
  };

  const handleCloseEditCorrection = () => {
    setEditingCorrection(null);
    closeEditCorrection();
  };

  const unresolvedErrorsCount = useMemo(() => {
    return errors.filter(
      (e: any) => !e.isResolved && !e.resolvedAt && e.status !== "resolved"
    ).length;
  }, [errors]);

  const { ref: containerRef, width: containerWidth } = useElementSize();
  const tabsListRef = useRef<HTMLDivElement>(null);
  // Safe initial estimate; replaced with real DOM measurement after first paint.
  const [measuredCoreWidth, setMeasuredCoreWidth] = useState(900);

  useEffect(() => {
    if (!tabsListRef.current) return;
    const tabs =
      tabsListRef.current.querySelectorAll<HTMLElement>('[role="tab"]');
    if (tabs.length < CORE_TAB_COUNT) return;
    let width = 0;
    for (let i = 0; i < CORE_TAB_COUNT; i += 1) {
      width += tabs[i].getBoundingClientRect().width;
    }
    setMeasuredCoreWidth(Math.ceil(width));
  }, [
    containerWidth,
    unresolvedErrorsCount,
    pendingRequests.length,
    discussions.length,
  ]);

  const OPTIONAL_TABS = useMemo(
    () => [
      {
        value: "communications",
        label: "Communications",
        badge: communications.length > 0 ? communications.length : null,
        badgeColor: "gray",
        estimatedWidth: 160,
      },
      {
        value: "sendouts",
        label: "Send-Outs",
        badge: null,
        badgeColor: undefined,
        estimatedWidth: 110,
      },
      {
        value: "qa-reviews",
        label: "QA Reviews",
        badge: qaReviews.length > 0 ? qaReviews.length : null,
        badgeColor: "violet",
        estimatedWidth: 130,
      },
      {
        value: "comments",
        label: `Comments (${standaloneComments.length})`,
        badge: null,
        badgeColor: undefined,
        estimatedWidth: 140,
      },
    ],
    [communications.length, qaReviews.length, standaloneComments.length]
  );

  // Reserve MORE_BUTTON_WIDTH so the dropdown never wraps when overflow tabs exist.
  const availableSpace = Math.max(
    0,
    containerWidth - measuredCoreWidth - MORE_BUTTON_WIDTH
  );

  const { visibleOptionalTabs, overflowTabs } = useMemo(() => {
    const visible: typeof OPTIONAL_TABS = [];
    const overflow: typeof OPTIONAL_TABS = [];
    let usedSpace = 0;

    if (containerWidth === 0) {
      return { visibleOptionalTabs: [], overflowTabs: OPTIONAL_TABS };
    }

    OPTIONAL_TABS.forEach((tab) => {
      if (usedSpace + tab.estimatedWidth + LAYOUT_TOLERANCE <= availableSpace) {
        visible.push(tab);
        usedSpace += tab.estimatedWidth;
      } else {
        overflow.push(tab);
      }
    });
    return { visibleOptionalTabs: visible, overflowTabs: overflow };
  }, [OPTIONAL_TABS, containerWidth, availableSpace]);

  const isOverflowActive = useMemo(
    () => overflowTabs.some((tab) => tab.value === activeTab),
    [overflowTabs, activeTab]
  );

  const overflowBadgeCount = useMemo(
    () => overflowTabs.reduce((sum, tab) => sum + (tab.badge || 0), 0),
    [overflowTabs]
  );

  const activeOverflowTab = isOverflowActive
    ? overflowTabs.find((t) => t.value === activeTab)
    : null;

  return (
    <Box
      ref={containerRef}
      style={{
        background: "white",
        borderRadius: 12,
        border: "1px solid var(--mantine-color-gray-2)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(v) => v && v !== "more" && onTabChange(v)}
      >
        <Tabs.List ref={tabsListRef}>
          <Tabs.Tab
            value="errors"
            fw={activeTab === "errors" ? 700 : 500}
            rightSection={
              unresolvedErrorsCount > 0 ? (
                <Badge color="red" size="xs" circle>
                  {unresolvedErrorsCount}
                </Badge>
              ) : null
            }
          >
            Errors
          </Tabs.Tab>
          <Tabs.Tab
            value="corrections"
            fw={activeTab === "corrections" ? 700 : 500}
            rightSection={
              pendingRequests.length > 0 ? (
                <Badge color="orange" size="xs" circle>
                  {pendingRequests.length}
                </Badge>
              ) : null
            }
          >
            Corrections
          </Tabs.Tab>
          <Tabs.Tab value="notes" fw={activeTab === "notes" ? 700 : 500}>
            Case Notes
          </Tabs.Tab>
          <Tabs.Tab value="files" fw={activeTab === "files" ? 700 : 500}>
            Files &amp; Uploads
          </Tabs.Tab>
          <Tabs.Tab
            value="discussions"
            fw={activeTab === "discussions" ? 700 : 500}
          >
            Internal Discussions ({discussions.length})
          </Tabs.Tab>
          <Tabs.Tab
            value="status-history"
            fw={activeTab === "status-history" ? 700 : 500}
          >
            Status History
          </Tabs.Tab>

          {visibleOptionalTabs.map((tab) => (
            <Tabs.Tab
              key={tab.value}
              value={tab.value}
              fw={activeTab === tab.value ? 700 : 500}
              rightSection={
                tab.badge ? (
                  <Badge color={tab.badgeColor} size="xs" circle>
                    {tab.badge}
                  </Badge>
                ) : undefined
              }
            >
              {tab.label}
            </Tabs.Tab>
          ))}

          {overflowTabs.length > 0 && (
            <Menu position="bottom-end" withinPortal offset={4}>
              <Menu.Target>
                <Tabs.Tab
                  value={isOverflowActive ? activeTab : "more"}
                  fw={isOverflowActive ? 700 : 500}
                  rightSection={
                    <Group gap={4} wrap="nowrap">
                      {overflowBadgeCount > 0 && (
                        <Badge color="blue" size="xs" circle>
                          {overflowBadgeCount}
                        </Badge>
                      )}
                      <IconChevronDown size={14} />
                    </Group>
                  }
                >
                  {isOverflowActive && activeOverflowTab
                    ? activeOverflowTab.label
                    : "More"}
                </Tabs.Tab>
              </Menu.Target>
              <Menu.Dropdown>
                {overflowTabs.map((tab) => (
                  <Menu.Item
                    key={tab.value}
                    onClick={() => onTabChange(tab.value)}
                    bg={
                      activeTab === tab.value
                        ? "var(--mantine-color-blue-0)"
                        : undefined
                    }
                    fw={activeTab === tab.value ? 700 : 500}
                    rightSection={
                      tab.badge ? (
                        <Badge color={tab.badgeColor} size="xs" circle>
                          {tab.badge}
                        </Badge>
                      ) : undefined
                    }
                  >
                    {tab.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
        </Tabs.List>

        <Box p="lg">
          <Tabs.Panel value="files">
            <FilesPanel
              caseId={caseId}
              files={files}
              onFileDownload={onFileDownload}
              onFileDelete={onFileDelete}
              uploadedDocFiles={uploadedDocFiles}
              onDocFilesChange={onDocFilesChange}
            />
          </Tabs.Panel>

          <Tabs.Panel value="discussions">
            <DiscussionsPanel discussions={discussions} caseId={caseId} />
          </Tabs.Panel>

          <Tabs.Panel value="notes">
            <NotesPanel
              notes={notes}
              isAddingNote={isAddingNote}
              setIsAddingNote={setIsAddingNote}
              newNoteText={newNoteText}
              setNewNoteText={setNewNoteText}
              editingNoteId={editingNoteId}
              setEditingNoteId={setEditingNoteId}
              editingNoteText={editingNoteText}
              setEditingNoteText={setEditingNoteText}
              onAddNote={onAddNote}
              onUpdateNote={onUpdateNote}
              onDeleteNote={onDeleteNote}
              createNoteLoading={createNoteLoading}
              updateNoteLoading={updateNoteLoading}
            />
          </Tabs.Panel>

          <Tabs.Panel value="status-history">
            <Stack gap="xl">
              <Box>
                <Text size="sm" fw={700} mb="md">
                  Case status history
                </Text>
                <CaseStatusHistoryPanel statusHistory={statusHistory} />
              </Box>
              <Divider label="Pathologist responsibility history" />
              <PathologistAssignmentHistory caseId={caseId} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="sendouts">
            <SendOutsPanel sendOuts={sendOuts} />
          </Tabs.Panel>

          <Tabs.Panel value="communications">
            <CommunicationsPanel
              communications={communications}
              caseId={caseId}
              caseData={caseData}
            />
          </Tabs.Panel>

          <Tabs.Panel value="qa-reviews">
            <QaReviewsPanel qaReviews={qaReviews} />
          </Tabs.Panel>

          <Tabs.Panel value="comments">
            <CommentsPanel
              caseId={caseId}
              standaloneComments={standaloneComments}
            />
          </Tabs.Panel>

          <Tabs.Panel value="errors">
            <ErrorsPanel
              errors={errors}
              onOpenResolveModal={onOpenResolveModal}
              onReportError={onReportError}
            />
          </Tabs.Panel>

          <Tabs.Panel value="corrections">
            {allRequests.length === 0 ? (
              <Box py="xl" style={{ textAlign: "center" }}>
                <IconAlertTriangle
                  size={40}
                  color="var(--mantine-color-gray-4)"
                  style={{ marginBottom: 8 }}
                />
                <Text size="sm" c="dimmed">
                  No correction requests for this case
                </Text>
              </Box>
            ) : (
              <Stack gap="md" style={{ maxWidth: 650 }}>
                {allRequests.map((req) => {
                  const isPending = req.status === "pending";
                  const isDirectCorrection = req.source === "direct_correction";
                  const displayChanges =
                    req.appliedChanges ?? req.proposedChanges;
                  const visibleChanges = Object.entries(
                    displayChanges ?? {}
                  ).filter(
                    ([, diff]: [string, any]) =>
                      String(diff?.before ?? "") !== String(diff?.after ?? "")
                  );
                  return (
                    <Card
                      key={req.id}
                      withBorder
                      style={{
                        borderColor: isPending
                          ? "var(--mantine-color-orange-4)"
                          : "var(--mantine-color-gray-2)",
                      }}
                    >
                      <Group justify="space-between" mb="xs">
                        <Box>
                          <Group gap="xs">
                            {correctionRefLabel(req.refNumber) && (
                              <Badge size="xs" variant="outline" color="blue">
                                {correctionRefLabel(req.refNumber)}
                              </Badge>
                            )}
                            <Text size="sm" fw={600}>
                              {isDirectCorrection
                                ? "Direct correction"
                                : req.requestor?.name}
                            </Text>
                          </Group>
                          <Text size="xs" c="dimmed">
                            {isDirectCorrection
                              ? `Updated by ${
                                  req.requestor?.name ?? "Unknown"
                                } on ${dayjs(req.requestedAt).format(
                                  "MMM D, YYYY h:mm A"
                                )}`
                              : dayjs(req.requestedAt).format(
                                  "MMM D, YYYY h:mm A"
                                )}
                          </Text>
                        </Box>
                        <Group gap="xs">
                          <Badge
                            color={
                              req.status === "pending"
                                ? "orange"
                                : req.status === "applied" ||
                                  req.status === "modified_and_applied"
                                ? "green"
                                : "gray"
                            }
                            variant="light"
                          >
                            {req.status.replace(/_/g, " ")}
                          </Badge>
                          {req.caseErrorReportId && (
                            <Tooltip label="Resolves a linked error report">
                              <Badge color="red" variant="outline" size="xs">
                                {req.caseErrorReportRefNumber != null
                                  ? `Linked to Error E${req.caseErrorReportRefNumber}`
                                  : "Linked to Error"}
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                      </Group>

                      {req.requestorNote &&
                        stripHtml(req.requestorNote) !== "(empty)" && (
                          <Alert color="blue" variant="light" mb="sm" p="xs">
                            <Box
                              className="widget-rich-text"
                              dangerouslySetInnerHTML={{
                                __html: req.requestorNote,
                              }}
                            />
                          </Alert>
                        )}

                      <Box mb="sm">
                        <Text size="xs" fw={600} c="dimmed" mb={4}>
                          {isPending ? "PROPOSED CHANGES" : "APPLIED CHANGES"}
                        </Text>
                        <Stack gap={4}>
                          {visibleChanges.length === 0 ? (
                            <Text size="xs" c="dimmed">
                              No field-level change details retained.
                            </Text>
                          ) : (
                            visibleChanges.map(
                              ([field, diff]: [string, any]) => (
                                <Group
                                  key={field}
                                  gap="xs"
                                  wrap="nowrap"
                                  align="flex-start"
                                >
                                  <Text
                                    size="xs"
                                    c="dimmed"
                                    style={{ minWidth: 180, flexShrink: 0 }}
                                  >
                                    {field.startsWith("cptCode.")
                                      ? "CPT Code"
                                      : FIELD_LABELS[field] ?? field}
                                  </Text>
                                  <Box
                                    className="rich-text-display"
                                    style={{
                                      color: "var(--mantine-color-red-6)",
                                      textDecoration: "line-through",
                                      flex: 1,
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        String(diff?.before ?? "") || "(empty)",
                                    }}
                                  />
                                  <Text size="xs" style={{ flexShrink: 0 }}>
                                    →
                                  </Text>
                                  <Box
                                    className="rich-text-display"
                                    style={{
                                      color: "var(--mantine-color-green-7)",
                                      fontWeight: 500,
                                      flex: 1,
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        String(diff?.after ?? "") || "(empty)",
                                    }}
                                  />
                                </Group>
                              )
                            )
                          )}
                        </Stack>
                      </Box>

                      {!isPending && req.resolvedBy && (
                        <>
                          <Divider mb="sm" />
                          <Group gap="xs" align="flex-start">
                            <Text size="xs" c="dimmed">
                              {isDirectCorrection ? "Updated" : "Resolved"} by{" "}
                              {req.resolvedBy.name} on{" "}
                              {dayjs(req.resolvedAt).format("MMM D, YYYY")}
                              {req.resolutionNote && " —"}
                            </Text>
                            {req.resolutionNote && (
                              <Box
                                className="rich-text-display"
                                dangerouslySetInnerHTML={{
                                  __html: req.resolutionNote,
                                }}
                              />
                            )}
                          </Group>
                        </>
                      )}

                      {!caseData?.isLegacyImported &&
                        isPending &&
                        (showReviewInPathologyButton ||
                          req.requestor?.id === user?.id) && (
                          <>
                            <Divider my="sm" />
                            <Group gap="xs">
                              {showReviewInPathologyButton && (
                                <Button
                                  size="xs"
                                  variant="light"
                                  onClick={() =>
                                    navigate(`/pathologist-review/${caseId}`)
                                  }
                                >
                                  Review
                                </Button>
                              )}
                              {req.requestor?.id === user?.id && (
                                <Button
                                  size="xs"
                                  variant="light"
                                  color="orange"
                                  leftSection={<IconEdit size={13} />}
                                  onClick={() => handleOpenEditCorrection(req)}
                                >
                                  Edit
                                </Button>
                              )}
                            </Group>
                          </>
                        )}
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Tabs.Panel>
        </Box>
      </Tabs>

      {!caseData?.isLegacyImported && editingCorrection && (
        <UpdatePatientInfoModal
          caseId={caseId}
          opened={editCorrectionOpened}
          onClose={handleCloseEditCorrection}
          isSignedOff
          caseData={caseData}
          editingCorrectionRequestId={editingCorrection.id}
          initialProposedChanges={editingCorrection.proposedChanges}
          initialCaseErrorReportId={editingCorrection.caseErrorReportId ?? null}
        />
      )}
    </Box>
  );
}

// ─── Internal Discussions Panel (read-only, links to discussion page) ───

function DiscussionsPanel({
  discussions,
  caseId,
}: {
  discussions: any[];
  caseId: string;
}) {
  const navigate = useNavigate();

  if (!discussions || discussions.length === 0) {
    return (
      <Box py="xl" style={{ textAlign: "center" }}>
        <IconMessageCircle
          size={40}
          color="var(--mantine-color-gray-4)"
          style={{ marginBottom: 8 }}
        />
        <Text size="sm" c="dimmed">
          No discussions flagged for this case
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="md">
      {discussions.map((discussion: any) => {
        const authorName = discussion.initiatedBy?.fullName || "Unknown User";
        const authorRole = discussion.initiatedBy?.role
          ? `(${discussion.initiatedBy.role})`
          : "";
        const color = getAvatarColor(authorName);

        return (
          <UnstyledButton
            key={discussion.id}
            onClick={() =>
              navigate(`/discussion-queue/${caseId}/${discussion.id}`)
            }
            style={{ display: "block", width: "100%" }}
          >
            <Box
              p="md"
              style={{
                background: "var(--mantine-color-gray-0)",
                borderRadius: 12,
                border: "1px solid var(--mantine-color-gray-1)",
                transition: "border-color 150ms",
                cursor: "pointer",
              }}
              className="hover-border-blue"
            >
              <Group
                align="flex-start"
                gap="sm"
                wrap="nowrap"
                justify="space-between"
              >
                <Avatar
                  size={40}
                  radius="xl"
                  style={{ backgroundColor: color, flexShrink: 0 }}
                >
                  <Text size="xs" fw={700} c="white">
                    {getInitials(authorName)}
                  </Text>
                </Avatar>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Box style={{ maxWidth: 720 }}>
                    <Group gap="xs" mb={4}>
                      <Text size="sm" fw={700}>
                        {authorName}
                      </Text>
                      {authorRole && (
                        <Text size="xs" c="dimmed">
                          {authorRole}
                        </Text>
                      )}
                      <Text size="xs" c="dimmed">
                        {dayjs(discussion.createdAt).fromNow()}
                      </Text>
                      {discussion.isUrgent && (
                        <Badge color="red" size="xs">
                          Urgent
                        </Badge>
                      )}
                      <Badge
                        color={
                          discussion.status === "resolved"
                            ? "green"
                            : discussion.status === "cancelled"
                            ? "gray"
                            : "blue"
                        }
                        size="xs"
                        variant="light"
                      >
                        {discussion.status}
                      </Badge>
                    </Group>
                    <Text size="sm" lineClamp={2}>
                      {discussion.reason}
                    </Text>
                    {discussion.assignedTo && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Assigned to {discussion.assignedTo.fullName}
                      </Text>
                    )}
                  </Box>
                </Box>
                <IconExternalLink
                  size={16}
                  color="var(--mantine-color-blue-5)"
                  style={{ flexShrink: 0, marginTop: 4 }}
                />
              </Group>
            </Box>
          </UnstyledButton>
        );
      })}
    </Stack>
  );
}

// ─── Notes Panel ────────────────────────────────────────────────────────

function NotesPanel({
  notes,
  isAddingNote,
  setIsAddingNote,
  newNoteText,
  setNewNoteText,
  editingNoteId,
  setEditingNoteId,
  editingNoteText,
  setEditingNoteText,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  createNoteLoading,
  updateNoteLoading,
}: {
  notes: any[];
  isAddingNote: boolean;
  setIsAddingNote: (v: boolean) => void;
  newNoteText: string;
  setNewNoteText: (v: string) => void;
  editingNoteId: string | null;
  setEditingNoteId: (v: string | null) => void;
  editingNoteText: string;
  setEditingNoteText: (v: string) => void;
  onAddNote: () => void;
  onUpdateNote: (noteId: string) => void;
  onDeleteNote: (noteId: string) => void;
  createNoteLoading: boolean;
  updateNoteLoading: boolean;
}) {
  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text size="sm" fw={700}>
          Clinical Case Notes
        </Text>
        {!isAddingNote && (
          <Button
            size="xs"
            fw={700}
            leftSection={<IconPlus size={14} />}
            onClick={() => setIsAddingNote(true)}
          >
            New Note
          </Button>
        )}
      </Group>

      {isAddingNote && (
        <Card withBorder p="md" mb="md">
          <Stack gap="sm">
            <Box style={{ maxWidth: 720 }}>
              <RichTextDiagnosis
                value={newNoteText}
                onChange={setNewNoteText}
                placeholder="Enter your note..."
              />
            </Box>
            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => {
                  setIsAddingNote(false);
                  setNewNoteText("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={onAddNote}
                loading={createNoteLoading}
                disabled={!newNoteText.replace(/<[^>]*>/g, "").trim()}
              >
                Save Note
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {notes.length === 0 && !isAddingNote ? (
        <Box py="xl" style={{ textAlign: "center" }}>
          <Text size="sm" c="dimmed" mb="sm">
            No notes yet
          </Text>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setIsAddingNote(true)}
          >
            Add First Note
          </Button>
        </Box>
      ) : (
        <Stack gap="md">
          {notes.map((note: any) => {
            const badge = getNoteBadge(note);
            return (
              <Box
                key={note.id}
                p="md"
                style={{
                  background: "var(--mantine-color-gray-0)",
                  borderRadius: 12,
                  border: "1px solid var(--mantine-color-gray-1)",
                }}
              >
                {editingNoteId === note.id ? (
                  <Stack gap="sm">
                    <Box style={{ maxWidth: 720 }}>
                      <RichTextDiagnosis
                        value={editingNoteText}
                        onChange={setEditingNoteText}
                      />
                    </Box>
                    <Group justify="flex-end" gap="xs">
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditingNoteText("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        onClick={() => onUpdateNote(note.id)}
                        loading={updateNoteLoading}
                        disabled={
                          !editingNoteText.replace(/<[^>]*>/g, "").trim()
                        }
                      >
                        Save Changes
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <>
                    <Group justify="space-between" mb={8}>
                      <Group gap={8}>
                        <Text size="sm" fw={700}>
                          {note.createdBy?.fullName || "Unknown"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {dayjs(note.createdAt).format("MMM D, YYYY • h:mm A")}
                        </Text>
                        <Badge color={badge.color} variant="light" size="xs">
                          {badge.label}
                        </Badge>
                      </Group>
                      <Group gap={4}>
                        {note.caseStatus && (
                          <Badge
                            color="gray"
                            variant="light"
                            size="xs"
                            tt="uppercase"
                          >
                            @ {note.caseStatus.name}
                          </Badge>
                        )}
                        <Tooltip label="Edit note">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setEditingNoteText(note.noteText);
                            }}
                          >
                            <IconEdit size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete note">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => onDeleteNote(note.id)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: 14,
                        maxWidth: 720,
                      }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: note.noteText || "",
                      }}
                    />
                  </>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

// ─── Files & Uploads Panel (horizontal card layout matching Stitch) ─────

function FilesPanel({
  caseId,
  files,
  onFileDownload,
  onFileDelete,
  uploadedDocFiles,
  onDocFilesChange,
}: {
  caseId: string;
  files: any[];
  onFileDownload: (file: { url: string; name: string }) => void;
  onFileDelete: (fileId: string) => void;
  uploadedDocFiles: UploadedFile[];
  onDocFilesChange: (files: UploadedFile[]) => void;
}) {
  const queryClient = useQueryClient();
  const [editingFile, setEditingFile] = useState<CaseFile | null>(null);
  const [previewFile, setPreviewFile] = useState<any | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewUrlRef = useRef<string | null>(null);
  const previewRequest = useRef(0);
  const releasePreviewUrl = useCallback(() => {
    if (previewUrlRef.current?.startsWith("blob:"))
      URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
  }, []);
  useEffect(
    () => () => {
      previewRequest.current += 1;
      releasePreviewUrl();
    },
    [releasePreviewUrl]
  );
  const handleOpenEdit = useCallback((file: CaseFile) => {
    setEditingFile(file);
  }, []);
  const handleCloseEdit = useCallback(() => {
    setEditingFile(null);
  }, []);
  const handleEditSaved = useCallback(() => {
    setEditingFile(null);
    queryClient.invalidateQueries({ queryKey: ["case-files", caseId] });
  }, [queryClient, caseId]);
  const handleViewFile = useCallback(
    async (file: any) => {
      if (!file?.url) return;
      releasePreviewUrl();
      previewRequest.current += 1;
      const request = previewRequest.current;
      setPreviewFile(file);
      setPreviewUrl(null);
      setPreviewLoading(true);
      try {
        const { url } = await caseImagesService.getDownloadUrl(
          file.url,
          file.name
        );
        if (previewRequest.current !== request) {
          if (url.startsWith("blob:")) URL.revokeObjectURL(url);
          return;
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } catch {
        if (previewRequest.current !== request) return;
        notifications.show({
          title: "Cannot open file",
          message: "Failed to get download URL for the document",
          color: "red",
        });
        setPreviewFile(null);
      } finally {
        if (previewRequest.current === request) setPreviewLoading(false);
      }
    },
    [releasePreviewUrl]
  );
  const handleClosePreview = useCallback(() => {
    previewRequest.current += 1;
    releasePreviewUrl();
    setPreviewFile(null);
    setPreviewUrl(null);
  }, [releasePreviewUrl]);
  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text size="sm" fw={700}>
          Case Documents &amp; Assets
        </Text>
        <DocumentUpload
          files={uploadedDocFiles}
          onFilesChange={onDocFilesChange}
          maxFiles={10}
          hideFileList
          hideButtons={false}
          actionsPosition="top"
        />
      </Group>

      {files.length === 0 &&
      uploadedDocFiles.filter((f) => !f.s3Key && !f.uploadError).length ===
        0 ? (
        <Box
          py={48}
          style={{
            textAlign: "center",
            border: "2px dashed var(--mantine-color-gray-3)",
            borderRadius: 12,
            background: "var(--mantine-color-gray-0)",
          }}
        >
          <IconUpload
            size={36}
            color="var(--mantine-color-gray-4)"
            style={{ marginBottom: 8 }}
          />
          <Text size="sm" c="dimmed" mb="xs">
            No files uploaded yet
          </Text>
          <Text size="xs" c="dimmed" mb="md">
            Drag and drop files or use the upload button above
          </Text>
          <DocumentUpload
            files={uploadedDocFiles}
            onFilesChange={onDocFilesChange}
            maxFiles={10}
            hideFileList
            hideButtons={false}
            actionsPosition="top"
          />
        </Box>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {files.map((file: any) => {
            const { icon: FileIcon, color } = getFileIcon(file);
            const size = formatFileSize(file.size || file.fileSize);
            return (
              <Box
                key={file.id}
                px="sm"
                py="sm"
                style={{
                  borderRadius: 8,
                  border: "1px solid var(--mantine-color-gray-2)",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <ThemeIcon
                  variant="light"
                  color={color}
                  size={40}
                  radius="md"
                  style={{ flexShrink: 0 }}
                >
                  <FileIcon size={20} />
                </ThemeIcon>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} lineClamp={1}>
                    {file.name}
                  </Text>
                  {file.description && (
                    <Text size="xs" c="dimmed" lineClamp={2} mt={2}>
                      {file.description}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">
                    {[size, dayjs(file.createdAt).format("MMM D, YYYY h:mm A")]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                  {file.documentType && (
                    <Badge
                      size="xs"
                      variant="light"
                      color={getUploadSourceBadge(file.documentType).color}
                      mt={4}
                    >
                      {getUploadSourceBadge(file.documentType).label}
                    </Badge>
                  )}
                </Box>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => handleViewFile(file)}
                  title="Preview"
                >
                  <IconEye size={16} />
                </ActionIcon>
                <Menu shadow="md" width={200} position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="sm">
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {file.documentType !== "legacy_report" && (
                      <Menu.Item
                        leftSection={<IconEdit size={14} />}
                        onClick={() => handleOpenEdit(file as CaseFile)}
                      >
                        Edit
                      </Menu.Item>
                    )}
                    <Menu.Item
                      leftSection={<IconDownload size={14} />}
                      onClick={() => onFileDownload(file)}
                    >
                      Download
                    </Menu.Item>
                    {file.documentType !== "accessioning" &&
                      file.documentType !== "legacy_report" && (
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => onFileDelete(file.id)}
                        >
                          Delete
                        </Menu.Item>
                      )}
                  </Menu.Dropdown>
                </Menu>
              </Box>
            );
          })}
          {uploadedDocFiles
            .filter((f) => !f.s3Key && !f.uploadError)
            .map((f) => {
              const name = f.file?.name || f.fileName || "Uploading...";
              return (
                <Box
                  key={name}
                  px="sm"
                  py="sm"
                  style={{
                    borderRadius: 8,
                    border: "1px solid var(--mantine-color-gray-2)",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <ThemeIcon
                    variant="light"
                    color="blue"
                    size={40}
                    radius="md"
                    style={{ flexShrink: 0 }}
                  >
                    <Loader size="xs" color="blue" />
                  </ThemeIcon>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} lineClamp={1}>
                      {name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Uploading...
                    </Text>
                  </Box>
                </Box>
              );
            })}
        </SimpleGrid>
      )}

      <CaseFileEditModal
        opened={!!editingFile}
        onClose={handleCloseEdit}
        caseId={caseId}
        file={editingFile}
        onSaved={handleEditSaved}
      />

      <Modal.Root
        opened={previewFile !== null}
        onClose={handleClosePreview}
        size="90%"
        centered
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>{previewFile?.name || "Preview"}</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body style={{ flex: 1, overflow: "auto", padding: 0 }}>
            {previewLoading || !previewUrl ? (
              <Center style={{ minHeight: 400 }}>
                <Loader />
              </Center>
            ) : previewFile?.mimeType?.startsWith("image/") ? (
              <Center style={{ padding: 16 }}>
                <img
                  src={previewUrl}
                  alt={previewFile?.name || "Preview"}
                  style={{ maxWidth: "100%", maxHeight: "70vh" }}
                />
              </Center>
            ) : (
              <iframe
                src={previewUrl}
                title={previewFile?.name || "Preview"}
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "none",
                  display: "block",
                }}
              />
            )}
          </Modal.Body>

          <Divider />
          <Group justify="flex-end" p="md">
            <Button variant="default" onClick={handleClosePreview}>
              Close
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </Box>
  );
}

// ─── Send-outs Panel (no create button) ─────────────────────────────────

function SendOutsPanel({ sendOuts }: { sendOuts: any[] }) {
  const [selectedSendOutId, setSelectedSendOutId] = useState<string | null>(
    null
  );
  const [detailOpened, { open: openDetail, close: closeDetail }] =
    useDisclosure(false);

  const handleRowClick = useCallback(
    (id: string) => {
      setSelectedSendOutId(id);
      openDetail();
    },
    [openDetail]
  );

  if (!sendOuts || sendOuts.length === 0) {
    return (
      <Box py="xl" style={{ textAlign: "center" }}>
        <IconExternalLink
          size={40}
          color="var(--mantine-color-gray-4)"
          style={{ marginBottom: 8 }}
        />
        <Text size="sm" c="dimmed" mb="xs">
          No send-out requests found
        </Text>
        <Text size="xs" c="dimmed">
          Send-outs are created by flagging for external opinion or external
          stain
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Destination</Table.Th>
            <Table.Th>Materials</Table.Th>
            <Table.Th>Requested By</Table.Th>
            <Table.Th>Requested</Table.Th>
            <Table.Th>Status</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sendOuts.map((sendOut: any) => {
            const statusBadge = getSendOutStatusBadge(sendOut.status);
            return (
              <Table.Tr
                key={sendOut.id}
                onClick={() => handleRowClick(sendOut.id)}
                style={{ cursor: "pointer" }}
              >
                <Table.Td>{formatRequestType(sendOut.requestType)}</Table.Td>
                <Table.Td>{getDestinationName(sendOut)}</Table.Td>
                <Table.Td>{getMaterialsSummary(sendOut)}</Table.Td>
                <Table.Td>{sendOut.requestedBy?.fullName || "—"}</Table.Td>
                <Table.Td>
                  <Tooltip
                    label={dayjs(sendOut.createdAt).format(
                      "MMM D, YYYY h:mm A"
                    )}
                  >
                    <Text size="sm">{dayjs(sendOut.createdAt).fromNow()}</Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Badge color={statusBadge.color} variant="light" size="sm">
                    {statusBadge.label}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <SendOutDetailModal
        opened={detailOpened}
        onClose={closeDetail}
        sendOutId={selectedSendOutId}
        readOnly
      />
    </>
  );
}

// ─── QA Reviews Panel ────────────────────────────────────────────────────

function getQaStatusBadge(status: string): { color: string; label: string } {
  switch (status) {
    case "completed":
      return { color: "green", label: "Completed" };
    case "in_review":
      return { color: "blue", label: "In Review" };
    case "pending":
      return { color: "yellow", label: "Pending" };
    default:
      return { color: "gray", label: status || "Unknown" };
  }
}

function getAgreementBadge(
  level: string | null
): { color: string; label: string } | null {
  if (!level) return null;
  switch (level) {
    case "complete":
      return { color: "green", label: "Complete Agreement" };
    case "minor_variation":
      return { color: "teal", label: "Minor Variation" };
    case "partial":
      return { color: "yellow", label: "Partial Agreement" };
    case "discrepancy":
      return { color: "orange", label: "Discrepancy" };
    case "major_discrepancy":
      return { color: "red", label: "Major Discrepancy" };
    default:
      return { color: "gray", label: level };
  }
}

function getActionLabel(action: string): string {
  switch (action) {
    case "none":
      return "None";
    case "notify_pathologist":
      return "Notify Pathologist";
    case "internal_discussion":
      return "Internal Discussion";
    case "amendment_needed":
      return "Amendment Needed";
    default:
      return action || "—";
  }
}

function QaReviewsPanel({ qaReviews }: { qaReviews: CaseQaReview[] }) {
  const [selectedQaReviewId, setSelectedQaReviewId] = useState<string | null>(
    null
  );
  const [detailOpened, { open: openDetail, close: closeDetail }] =
    useDisclosure(false);

  const handleRowClick = useCallback(
    (id: string) => {
      setSelectedQaReviewId(id);
      openDetail();
    },
    [openDetail]
  );

  if (!qaReviews || qaReviews.length === 0) {
    return (
      <Box py="xl" style={{ textAlign: "center" }}>
        <IconScale
          size={40}
          color="var(--mantine-color-gray-4)"
          style={{ marginBottom: 8 }}
        />
        <Text size="sm" c="dimmed" mb="xs">
          No QA reviews for this case
        </Text>
        <Text size="xs" c="dimmed">
          QA reviews are created when external opinion reports are received
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Status</Table.Th>
            <Table.Th>Institution</Table.Th>
            <Table.Th>Reviewer</Table.Th>
            <Table.Th>Original Pathologist</Table.Th>
            <Table.Th>Agreement</Table.Th>
            <Table.Th>Action</Table.Th>
            <Table.Th>Date</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {qaReviews.map((review) => {
            const statusBadge = getQaStatusBadge(review.status);
            const agreementBadge = getAgreementBadge(review.agreementLevel);
            const displayDate = review.completedAt || review.createdAt;

            return (
              <Table.Tr
                key={review.id}
                onClick={() => handleRowClick(review.id)}
                style={{ cursor: "pointer" }}
              >
                <Table.Td>
                  <Badge color={statusBadge.color} variant="light" size="sm">
                    {statusBadge.label}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {review.sendOut?.externalInstitution?.name || "—"}
                </Table.Td>
                <Table.Td>{review.reviewedBy?.fullName || "—"}</Table.Td>
                <Table.Td>
                  {review.originalPathologist?.fullName || "—"}
                </Table.Td>
                <Table.Td>
                  {agreementBadge ? (
                    <Badge
                      color={agreementBadge.color}
                      variant="light"
                      size="sm"
                    >
                      {agreementBadge.label}
                    </Badge>
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {review.status === "completed"
                      ? getActionLabel(review.recommendedAction)
                      : "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Tooltip
                    label={dayjs(displayDate).format("MMM D, YYYY h:mm A")}
                  >
                    <Text size="sm">{dayjs(displayDate).fromNow()}</Text>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <QaReviewModal
        opened={detailOpened}
        onClose={closeDetail}
        qaReviewId={selectedQaReviewId}
      />
    </>
  );
}

// ─── Comments Panel (styled like Notes with rich text) ──────────────────

function CommentsPanel({
  caseId,
  standaloneComments,
}: {
  caseId: string;
  standaloneComments: any[];
}) {
  const { user } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");

  const addMutation = useAddComment(
    user ? { id: user.id, fullName: user.fullName } : undefined
  );
  const updateMutation = useUpdateComment();
  const deleteMutation = useDeleteComment();

  const isContentEmpty = (html: string) => !html.replace(/<[^>]*>/g, "").trim();

  const handleAdd = () => {
    if (isContentEmpty(newComment) || !caseId) return;
    addMutation.mutate(
      { caseId, content: newComment, isInternal: true },
      {
        onSuccess: () => {
          setNewComment("");
          setIsAdding(false);
          notifications.show({
            title: "Comment added",
            message: "Your comment has been posted.",
            color: "teal",
          });
        },
      }
    );
  };

  const handleUpdate = (commentId: string) => {
    if (isContentEmpty(editingContent)) return;
    updateMutation.mutate(
      { commentId, data: { content: editingContent } },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditingContent("");
        },
      }
    );
  };

  const handleDelete = (commentId: string) => {
    deleteMutation.mutate(commentId);
  };

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text size="sm" fw={700}>
          Case Comments
        </Text>
        {!isAdding && (
          <Button
            size="xs"
            fw={700}
            color="teal"
            leftSection={<IconPlus size={14} />}
            onClick={() => setIsAdding(true)}
          >
            New Comment
          </Button>
        )}
      </Group>

      {isAdding && (
        <Card withBorder p="md" mb="md">
          <Stack gap="sm">
            <Box style={{ maxWidth: 720 }}>
              <RichTextDiagnosis
                value={newComment}
                onChange={setNewComment}
                placeholder="Write your comment..."
              />
            </Box>
            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => {
                  setIsAdding(false);
                  setNewComment("");
                }}
              >
                Cancel
              </Button>
              <Button
                color="teal"
                onClick={handleAdd}
                loading={addMutation.isPending}
                disabled={isContentEmpty(newComment)}
              >
                Post Comment
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {standaloneComments.length === 0 && !isAdding ? (
        <Box py="xl" style={{ textAlign: "center" }}>
          <IconMessageCircle
            size={40}
            color="var(--mantine-color-gray-4)"
            style={{ marginBottom: 8 }}
          />
          <Text size="sm" c="dimmed" mb="sm">
            No comments yet
          </Text>
          <Button
            variant="light"
            size="xs"
            color="teal"
            leftSection={<IconPlus size={14} />}
            onClick={() => setIsAdding(true)}
          >
            Add First Comment
          </Button>
        </Box>
      ) : (
        <Stack gap="md">
          {standaloneComments.map((comment: any) => {
            const authorName =
              comment.user?.fullName ||
              comment.createdBy?.fullName ||
              "Unknown";

            return (
              <Box
                key={comment.id}
                p="md"
                style={{
                  background: "var(--mantine-color-gray-0)",
                  borderRadius: 12,
                  border: "1px solid var(--mantine-color-gray-1)",
                }}
              >
                {editingId === comment.id ? (
                  <Stack gap="sm">
                    <Box style={{ maxWidth: 720 }}>
                      <RichTextDiagnosis
                        value={editingContent}
                        onChange={setEditingContent}
                      />
                    </Box>
                    <Group justify="flex-end" gap="xs">
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => {
                          setEditingId(null);
                          setEditingContent("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        color="teal"
                        onClick={() => handleUpdate(comment.id)}
                        loading={updateMutation.isPending}
                        disabled={isContentEmpty(editingContent)}
                      >
                        Save Changes
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <>
                    <Group justify="space-between" mb={8}>
                      <Group gap={8}>
                        <Text size="sm" fw={700}>
                          {authorName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {dayjs(comment.createdAt).format(
                            "MMM D, YYYY • h:mm A"
                          )}
                        </Text>
                        <Badge color="teal" variant="light" size="xs">
                          Comment
                        </Badge>
                      </Group>
                      <Group gap={4}>
                        <Tooltip label="Edit comment">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => {
                              setEditingId(comment.id);
                              setEditingContent(comment.content || "");
                            }}
                          >
                            <IconEdit size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete comment">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => handleDelete(comment.id)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: 14,
                        maxWidth: 720,
                      }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: comment.content || "",
                      }}
                    />
                  </>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

// ─── Errors Panel (icon-on-left layout, red severity — Stitch HTML) ────

const ERROR_TYPE_LABELS: Record<string, string> = {
  SpecNotRcvd: "Specimen Not Received",
  PatData: "Patient Data Missing",
  NoSpec: "No Specimen in Container",
};

const ERROR_SEVERITY_LABELS: Record<string, string> = {
  critical: "Critical",
  major: "Major",
  minor: "Minor",
};

function ErrorsPanel({
  errors,
  onOpenResolveModal,
  onReportError,
}: {
  errors: any[];
  onOpenResolveModal: (e: any) => void;
  onReportError: () => void;
}) {
  const navigate = useNavigate();
  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Group gap={8}>
          <IconAlertOctagon size={18} color="var(--mantine-color-red-5)" />
          <Text size="sm" fw={700}>
            Data Validation Errors ({errors.length})
          </Text>
        </Group>
        <Button
          variant="light"
          color="orange"
          size="xs"
          fw={700}
          leftSection={<IconAlertTriangle size={14} />}
          onClick={onReportError}
        >
          Report Error
        </Button>
      </Group>

      {errors.length === 0 ? (
        <Box py="xl" style={{ textAlign: "center" }}>
          <Text size="sm" c="dimmed">
            No errors reported
          </Text>
        </Box>
      ) : (
        <Stack gap="md">
          {errors.map((errorReport: any) => {
            const isResolved =
              errorReport.status === "resolved" ||
              errorReport.isResolved ||
              !!errorReport.resolvedAt;

            const rawSeverity =
              errorReport.severity ||
              errorReport.error?.severity ||
              errorReport.discrepancy?.severity ||
              "critical";
            const severityLabel =
              ERROR_SEVERITY_LABELS[rawSeverity] ?? rawSeverity;

            const rawErrorTitle =
              errorReport.error?.error || errorReport.discrepancy?.error || "";
            const errorTitle = rawErrorTitle
              ? ERROR_TYPE_LABELS[rawErrorTitle] ?? rawErrorTitle
              : "Error";

            return (
              <Box
                key={errorReport.id}
                p="md"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 16,
                  background: isResolved
                    ? "var(--mantine-color-gray-0)"
                    : "var(--mantine-color-red-0)",
                  borderRadius: 8,
                  border: `1px solid ${
                    isResolved
                      ? "var(--mantine-color-gray-2)"
                      : "var(--mantine-color-red-1)"
                  }`,
                }}
              >
                {/* Icon box on left */}
                <Box
                  style={{
                    padding: 8,
                    background: isResolved
                      ? "var(--mantine-color-gray-1)"
                      : "var(--mantine-color-red-1)",
                    borderRadius: 6,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconAlertOctagon
                    size={20}
                    color={
                      isResolved
                        ? "var(--mantine-color-gray-5)"
                        : "var(--mantine-color-red-6)"
                    }
                  />
                </Box>

                {/* Content */}
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Group justify="space-between" align="flex-start" mb={4}>
                    <Group gap="xs">
                      {errorRefLabel(errorReport.refNumber) && (
                        <Badge size="xs" variant="outline" color="blue">
                          {errorRefLabel(errorReport.refNumber)}
                        </Badge>
                      )}
                      <Text
                        fw={700}
                        size="sm"
                        c={
                          isResolved ? undefined : "var(--mantine-color-red-8)"
                        }
                      >
                        {errorTitle}
                      </Text>
                    </Group>
                    {!isResolved && (
                      <Text
                        size="xs"
                        fw={700}
                        c="var(--mantine-color-red-4)"
                        tt="uppercase"
                        style={{ flexShrink: 0 }}
                      >
                        {severityLabel}
                      </Text>
                    )}
                    {isResolved && (
                      <Badge color="green" size="xs" variant="light">
                        Resolved
                      </Badge>
                    )}
                  </Group>

                  {errorReport.description && (
                    <div
                      style={{
                        fontSize: 14,
                        color: isResolved
                          ? "var(--mantine-color-gray-7)"
                          : "rgba(153, 27, 27, 0.8)",
                        marginBottom: 8,
                        maxWidth: 720,
                      }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(errorReport.description || ""),
                      }}
                    />
                  )}

                  {!isResolved && (
                    <Button
                      color="green"
                      size="xs"
                      mt="sm"
                      onClick={() => onOpenResolveModal(errorReport)}
                    >
                      Resolve
                    </Button>
                  )}

                  {/* Resolution details */}
                  {errorReport.resolvedAt && errorReport.resolvedBy && (
                    <Box
                      mt="sm"
                      p="sm"
                      style={{
                        background: "var(--mantine-color-green-0)",
                        borderRadius: 6,
                        border: "1px solid var(--mantine-color-green-2)",
                      }}
                    >
                      <Text size="xs" fw={600} mb={4}>
                        Resolution Details
                      </Text>
                      <Text size="xs" c="dimmed">
                        Resolved by: {errorReport.resolvedBy.fullName} on{" "}
                        {dayjs(errorReport.resolvedAt).format(
                          "MMM D, YYYY h:mm A"
                        )}
                      </Text>
                      {errorReport.resolutionNotes && (
                        <div
                          style={{ fontSize: 14, marginTop: 4, maxWidth: 720 }}
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                              errorReport.resolutionNotes || ""
                            ),
                          }}
                        />
                      )}
                      {errorReport.correctionRequestId && (
                        <Button
                          variant="subtle"
                          size="xs"
                          color="blue"
                          mt={6}
                          onClick={() =>
                            navigate(
                              `/cases/${errorReport.caseId}?tab=corrections`
                            )
                          }
                        >
                          {errorReport.correctionRequest?.refNumber != null
                            ? `View Linked Correction C${errorReport.correctionRequest.refNumber}`
                            : "View Linked Correction"}
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

// ─── Communications Panel ───────────────────────────────────────

function CommunicationsPanel({
  communications,
  caseId,
  caseData,
}: {
  communications: UnifiedCommunication[];
  caseId: string;
  caseData: any;
}) {
  const [logModalOpened, { open: openLogModal, close: closeLogModal }] =
    useDisclosure(false);

  if (!communications || communications.length === 0) {
    return (
      <Box py="xl" style={{ textAlign: "center" }}>
        <IconMail
          size={40}
          color="var(--mantine-color-gray-4)"
          style={{ marginBottom: 12 }}
        />
        <Text size="sm" c="dimmed" mb="md">
          No communications logged
        </Text>
        <Button
          variant="light"
          size="sm"
          leftSection={<IconPlus size={14} />}
          onClick={openLogModal}
        >
          Log Communication
        </Button>
        <LogCommunicationModal
          opened={logModalOpened}
          onClose={closeLogModal}
          caseId={caseId}
          caseData={caseData}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Group justify="flex-end" mb="md">
        <Button
          variant="light"
          size="sm"
          leftSection={<IconPlus size={14} />}
          onClick={openLogModal}
        >
          Log Communication
        </Button>
      </Group>

      <Stack gap="sm">
        {communications.map((comm) => (
          <Box
            key={comm.id}
            p="md"
            style={{
              background: "var(--mantine-color-gray-0)",
              borderRadius: 8,
              border: "1px solid var(--mantine-color-gray-2)",
            }}
          >
            <Group justify="space-between" mb={6} wrap="nowrap">
              <Group gap="sm" wrap="nowrap">
                <Badge
                  size="sm"
                  variant="light"
                  color={comm.type === "call" ? "green" : "blue"}
                  leftSection={
                    comm.type === "call" ? (
                      <IconPhone size={10} />
                    ) : (
                      <IconMail size={10} />
                    )
                  }
                >
                  {comm.type === "call" ? "Call" : "Email"}
                </Badge>
                {comm.contactPerson && (
                  <Text size="sm" fw={600}>
                    {comm.contactPerson}
                  </Text>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {dayjs(comm.createdAt).fromNow()}
              </Text>
            </Group>

            {comm.type === "email" && comm.subject && (
              <Text size="sm" c="dimmed" mb={4}>
                Subject: {comm.subject}
              </Text>
            )}

            <Text
              size="sm"
              style={{ lineHeight: 1.6, whiteSpace: "pre-wrap", maxWidth: 720 }}
            >
              {comm.notes}
            </Text>

            <Group gap="xs" mt="sm">
              {comm.loggedBy && (
                <Text size="xs" c="dimmed">
                  Logged by {comm.loggedBy.fullName}
                </Text>
              )}
              <Text size="xs" c="dimmed">
                &middot;
              </Text>
              <Badge
                size="xs"
                variant="outline"
                color={comm.source === "case" ? "gray" : "violet"}
              >
                {comm.source === "case"
                  ? "Case"
                  : comm.sendOutLabel || "Send-out"}
              </Badge>
            </Group>
          </Box>
        ))}
      </Stack>

      <LogCommunicationModal
        opened={logModalOpened}
        onClose={closeLogModal}
        caseId={caseId}
        caseData={caseData}
      />
    </Box>
  );
}

// ─── Log Communication Modal ────────────────────────────────────

function LogCommunicationModal({
  opened,
  onClose,
  caseId,
  caseData,
}: {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  caseData: any;
}) {
  const addMutation = useAddCommunicationLog(caseId);

  const contactOptions = (() => {
    const seen = new Set<string>();
    const add = (val: string) => {
      if (val && !seen.has(val)) {
        seen.add(val);
      }
    };
    const physician = caseData?.physician;
    if (physician) {
      add(`Dr. ${physician.firstName} ${physician.lastName}`);
    }
    const patient = caseData?.patient;
    if (patient) {
      add(`${patient.firstName} ${patient.lastName} (Patient)`);
    }
    const practice = caseData?.sendingPractice;
    if (practice?.name) add(practice.name);
    const surgeryCenter = caseData?.surgeryCenterLab;
    if (surgeryCenter?.name) add(surgeryCenter.name);
    const pathologist = caseData?.pathologist;
    if (pathologist?.fullName) add(`${pathologist.fullName} (Pathologist)`);
    return Array.from(seen);
  })();

  const defaultContact = caseData?.physician
    ? `Dr. ${caseData.physician.firstName} ${caseData.physician.lastName}`
    : "";

  const form = useForm({
    initialValues: {
      type: "call" as "call" | "email",
      contactPerson: defaultContact,
      subject: "",
      notes: "",
    },
    validate: {
      notes: (v: string) => (!v.trim() ? "Notes are required" : null),
    },
  });

  useEffect(() => {
    if (opened) {
      form.reset();
      form.setFieldValue("contactPerson", defaultContact);
    }
  }, [opened, defaultContact, form]);

  const handleSubmit = async (values: typeof form.values) => {
    try {
      await addMutation.mutateAsync({
        type: values.type,
        contactPerson: values.contactPerson || undefined,
        subject:
          values.type === "email" ? values.subject || undefined : undefined,
        notes: values.notes,
      });
      notifications.show({
        title: "Communication Logged",
        message: `${
          values.type === "call" ? "Call" : "Email"
        } logged successfully`,
        color: "green",
      });
      form.reset();
      onClose();
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to log communication",
        color: "red",
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Log Communication</Text>}
      size="md"
      centered
      closeOnClickOutside={false}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Radio.Group
            label="Type"
            value={form.values.type}
            onChange={(val) =>
              form.setFieldValue("type", val as "call" | "email")
            }
          >
            <Group mt="xs">
              <Radio
                value="call"
                label={
                  <Group gap="xs">
                    <IconPhone size={14} />
                    <Text size="sm">Phone Call</Text>
                  </Group>
                }
              />
              <Radio
                value="email"
                label={
                  <Group gap="xs">
                    <IconMail size={14} />
                    <Text size="sm">Email</Text>
                  </Group>
                }
              />
            </Group>
          </Radio.Group>

          <Autocomplete
            label={form.values.type === "call" ? "Contact Person" : "Recipient"}
            placeholder={
              form.values.type === "call"
                ? "Name of person contacted"
                : "Email recipient name"
            }
            data={contactOptions}
            {...form.getInputProps("contactPerson")}
          />

          {form.values.type === "email" && (
            <TextInput
              label="Subject"
              placeholder="Email subject line (optional)"
              {...form.getInputProps("subject")}
            />
          )}

          <Textarea
            label="Notes"
            placeholder={
              form.values.type === "call"
                ? "Describe the call - who you spoke with, outcome, next steps..."
                : "Describe the email content and any response received..."
            }
            minRows={3}
            withAsterisk
            {...form.getInputProps("notes")}
          />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color={form.values.type === "call" ? "blue" : "grape"}
              loading={addMutation.isPending}
            >
              Log Communication
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
